import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const FUEL_OPTIONS = ['1/4', '1/2', '3/4', 'Lleno'];

const WarningLightsPanel = ({ warningLights, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const warningLightsList = [
    { id: 'checkEngine', label: 'Check Engine', icon: 'AlertTriangle', category: 'motor' },
    { id: 'abs', label: 'ABS', icon: 'Circle', category: 'frenos' },
    { id: 'airbag', label: 'Airbag', icon: 'Shield', category: 'seguridad' },
    { id: 'battery', label: 'Batería', icon: 'Battery', category: 'electrico' },
    { id: 'oilPressure', label: 'Presión de Aceite', icon: 'Droplet', category: 'motor' },
    { id: 'coolant', label: 'Temperatura del Motor', icon: 'Thermometer', category: 'motor' },
    { id: 'brakeFluid', label: 'Líquido de Frenos', icon: 'AlertCircle', category: 'frenos' },
    { id: 'tirePressure', label: 'Presión de Llantas', icon: 'Circle', category: 'llantas' },
    { id: 'traction', label: 'Control de Tracción', icon: 'Zap', category: 'seguridad' },
    { id: 'stability', label: 'Control de Estabilidad', icon: 'Navigation', category: 'seguridad' },
    { id: 'seatbelt', label: 'Cinturón de Seguridad', icon: 'Shield', category: 'seguridad' },
    { id: 'doorOpen', label: 'Puerta Abierta', icon: 'DoorOpen', category: 'general' },
    { id: 'washerFluid', label: 'Líquido Limpiaparabrisas', icon: 'Droplets', category: 'general' },
    { id: 'transmission', label: 'Transmisión', icon: 'Settings', category: 'motor' },
    { id: 'parkingBrake', label: 'Freno de Mano', icon: 'Hand', category: 'frenos' }
  ];

  const categories = [
    { id: 'all', label: 'Todos', icon: 'Grid' },
    { id: 'motor', label: 'Motor', icon: 'Wrench' },
    { id: 'frenos', label: 'Frenos', icon: 'Circle' },
    { id: 'seguridad', label: 'Seguridad', icon: 'Shield' },
    { id: 'electrico', label: 'Eléctrico', icon: 'Zap' },
    { id: 'llantas', label: 'Llantas', icon: 'Circle' },
    { id: 'general', label: 'General', icon: 'MoreHorizontal' }
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');

  const handleToggle = (lightId) => {
    const updatedLights = { ...warningLights };
    if (updatedLights?.[lightId]) {
      delete updatedLights?.[lightId];
    } else {
      updatedLights[lightId] = true;
    }
    onChange(updatedLights);
  };

  const handleFuelChange = (value) => {
    const updatedLights = { ...warningLights };
    if (value) {
      updatedLights['fuelLevel'] = value;
    } else {
      delete updatedLights?.['fuelLevel'];
    }
    onChange(updatedLights);
  };

  const filteredLights = warningLightsList?.filter(light => {
    const matchesCategory = selectedCategory === 'all' || light?.category === selectedCategory;
    const matchesSearch = light?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Show fuel selector in general or all category
  const showFuelSelector = (selectedCategory === 'all' || selectedCategory === 'general') &&
    ('medida de combustible'?.includes(searchTerm?.toLowerCase()) || !searchTerm?.trim());

  const activeCount = Object.keys(warningLights)?.filter(k => k !== 'fuelLevel')?.length;
  const fuelValue = warningLights?.['fuelLevel'] || '';

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-error/10">
            <Icon name="AlertOctagon" size={24} className="text-error" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
              Testigos del Tablero
            </h2>
            {activeCount > 0 && (
              <p className="text-sm caption text-error">
                {activeCount} testigo(s) encendido(s)
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="mb-4">
        <div className="relative">
          <Icon 
            name="Search" 
            size={20} 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Buscar testigo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="
              w-full pl-10 pr-4 py-2 rounded-lg
              bg-muted border border-border
              text-foreground placeholder:text-muted-foreground
              focus:outline-none focus:ring-2 focus:ring-ring
              transition-smooth
            "
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {categories?.map((category) => (
          <button
            key={category?.id}
            type="button"
            onClick={() => setSelectedCategory(category?.id)}
            className={`
              flex items-center gap-2 px-3 py-1.5 rounded-lg
              text-sm font-body font-medium
              transition-smooth press-effect
              ${selectedCategory === category?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground hover:bg-muted/80'
              }
            `}
          >
            <Icon name={category?.icon} size={16} />
            <span>{category?.label}</span>
          </button>
        ))}
      </div>
      {/* Medida de combustible selector — shown in 'all' and 'general' categories */}
      {showFuelSelector && (
        <div className="mb-4 p-3 rounded-lg border border-border bg-muted/30">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Fuel" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Medida de combustible</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {FUEL_OPTIONS?.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleFuelChange(fuelValue === option ? '' : option)}
                className={`
                  px-4 py-1.5 rounded-lg text-sm font-medium border transition-smooth press-effect
                  ${fuelValue === option
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                  }
                `}
              >
                {option}
              </button>
            ))}
            {fuelValue && (
              <button
                type="button"
                onClick={() => handleFuelChange('')}
                className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground border border-border hover:bg-muted transition-smooth"
              >
                Limpiar
              </button>
            )}
          </div>
          {fuelValue && (
            <p className="text-xs text-muted-foreground mt-1">Seleccionado: <span className="font-medium text-foreground">{fuelValue}</span></p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
        {filteredLights?.map((light) => (
          <div
            key={light?.id}
            className={`
              p-3 rounded-lg border transition-smooth
              ${warningLights?.[light?.id]
                ? 'bg-error/10 border-error/20' :'bg-muted/30 border-border'
              }
            `}
          >
            <Checkbox
              label={
                <div className="flex items-center gap-2">
                  <Icon 
                    name={light?.icon} 
                    size={16} 
                    className={warningLights?.[light?.id] ? 'text-error' : 'text-muted-foreground'}
                  />
                  <span>{light?.label}</span>
                </div>
              }
              checked={warningLights?.[light?.id] || false}
              onChange={() => handleToggle(light?.id)}
            />
          </div>
        ))}
      </div>
      {filteredLights?.length === 0 && !showFuelSelector && (
        <div className="text-center py-8">
          <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm caption text-muted-foreground">
            No se encontraron testigos con ese criterio
          </p>
        </div>
      )}
      {activeCount > 0 && (
        <div className="mt-4 p-4 rounded-lg bg-error/10 border border-error/20">
          <div className="flex items-start gap-2">
            <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
            <p className="text-sm caption text-error">
              Se detectaron testigos encendidos. Asegúrese de documentar estos hallazgos en la inspección técnica.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarningLightsPanel;